import React, { createContext, useContext } from 'react';
import { Box, BoxProps } from '@chakra-ui/react';
import { CheckCircle, Info, Warning, WarningCircle } from 'phosphor-react';

type IconType = React.ComponentType<{ size?: number | string; weight?: any }>;

export type AlertVariant = 'default' | 'info' | 'success' | 'warning' | 'error';

interface VariantStyle {
  bg: string;
  borderColor: string;
  iconColor: string;
  titleColor: string;
  descriptionColor: string;
  icon: IconType;
}

const variantStyles: Record<AlertVariant, VariantStyle> = {
  default: {
    bg: 'surface.base',
    borderColor: 'line.input',
    iconColor: 'content.strong',
    titleColor: 'content.strong',
    descriptionColor: 'content.body',
    icon: Info,
  },
  info: {
    bg: 'rgba(19, 145, 201, 0.04)',
    borderColor: 'rgba(19, 145, 201, 0.32)',
    iconColor: 'state.link',
    titleColor: 'content.strong',
    descriptionColor: 'content.body',
    icon: Info,
  },
  success: {
    bg: 'rgba(21, 176, 66, 0.04)',
    borderColor: 'rgba(21, 176, 66, 0.32)',
    iconColor: 'state.success',
    titleColor: 'content.strong',
    descriptionColor: 'content.body',
    icon: CheckCircle,
  },
  warning: {
    bg: 'rgba(217, 119, 46, 0.04)',
    borderColor: 'rgba(217, 119, 46, 0.32)',
    iconColor: 'state.warning',
    titleColor: 'content.strong',
    descriptionColor: 'content.body',
    icon: Warning,
  },
  error: {
    bg: 'rgba(214, 61, 61, 0.04)',
    borderColor: 'rgba(214, 61, 61, 0.32)',
    iconColor: 'state.destructive',
    titleColor: 'content.strong',
    descriptionColor: 'content.body',
    icon: WarningCircle,
  },
};

const AlertContext = createContext<VariantStyle>(variantStyles.default);

export interface AlertProps extends Omit<BoxProps, 'title'> {
  variant?: AlertVariant;
  icon?: IconType | null;
}

export function Alert({ variant = 'default', icon, children, ...rest }: AlertProps) {
  const styles = variantStyles[variant];
  const IconComponent = icon === null ? null : icon ?? styles.icon;
  const items = React.Children.toArray(children);
  const actions = items.filter(
    (child) => React.isValidElement(child) && child.type === AlertAction
  );
  const content = items.filter(
    (child) => !(React.isValidElement(child) && child.type === AlertAction)
  );

  return (
    <AlertContext.Provider value={styles}>
      <Box
        role="alert"
        display="flex"
        alignItems="flex-start"
        gap="12px"
        width="100%"
        px="14px"
        py="12px"
        border="1px solid"
        borderColor={styles.borderColor}
        borderRadius="lg"
        bg={styles.bg}
        {...rest}
      >
        {IconComponent && (
          <Box display="flex" mt="2px" color={styles.iconColor} flexShrink={0}>
            <IconComponent size={16} />
          </Box>
        )}
        <Box
          display="flex"
          flexDirection={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'stretch', md: 'center' }}
          justifyContent="space-between"
          gap="12px"
          flex="1"
          minWidth={0}
        >
          <Box display="flex" flexDirection="column" gap="2px" minWidth={0}>
            {content}
          </Box>
          {actions}
        </Box>
      </Box>
    </AlertContext.Provider>
  );
}

export function AlertTitle({ children, ...rest }: BoxProps) {
  const styles = useContext(AlertContext);

  return (
    <Box fontSize="13px" lineHeight="20px" fontWeight={500} color={styles.titleColor} {...rest}>
      {children}
    </Box>
  );
}

export function AlertDescription({ children, ...rest }: BoxProps) {
  const styles = useContext(AlertContext);

  return (
    <Box fontSize="13px" lineHeight="20px" color={styles.descriptionColor} {...rest}>
      {children}
    </Box>
  );
}

export function AlertAction({ children, ...rest }: BoxProps) {
  return (
    <Box display="flex" alignItems="center" gap="8px" flexShrink={0} {...rest}>
      {children}
    </Box>
  );
}
